import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useAppContext } from '../../libs/appContextLib';
import { getVisibleRoutes } from '../../libs/navigationLogic';
import { getDefaultPins } from '../../libs/navigationOnboarding';
import { useRouteRegistry } from '../../queries/useRouteRegistry';
import { useUserRoutePreferences } from '../../queries/useUserRoutePreferences';

const BANNER_VERSION = 'v3';
const FALLBACK_PRIORITY = 999;
const PIN_SUGGESTION_ROUTE_IDS = ['business-digest', 'gtm-stats'];

const BannerContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})(({ theme, isVisible }) => ({
  width: '100%',
  position: 'relative',
  borderRadius: 0,
  border: 'none',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  marginBottom: 0,
  animation: isVisible ? 'slideDown 0.25s ease-out' : 'none',
  '@keyframes slideDown': {
    from: {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const isPinnableRoute = (route) => Boolean(route && !route.isPermanent);

const sortByPriority = (left, right) => {
  const leftPriority = Number.isFinite(left?.priority) ? left.priority : FALLBACK_PRIORITY;
  const rightPriority = Number.isFinite(right?.priority) ? right.priority : FALLBACK_PRIORITY;
  return leftPriority - rightPriority;
};

const uniqueById = (routes = []) => {
  const seenIds = new Set();
  return routes.filter((route) => {
    const routeId = route?.id;
    if (!routeId || seenIds.has(routeId)) return false;
    seenIds.add(routeId);
    return true;
  });
};

export const GENERIC_PIN_RECOMMENDATION = {
  type: 'generic',
  id: 'most-used-view',
  label: 'your most-used view',
};

export const computePinnedViewRecommendations = ({ role, visibleRoutes = [] }) => {
  const pinnableVisibleRoutes = uniqueById(visibleRoutes)
    .filter(isPinnableRoute)
    .sort(sortByPriority);

  const roleDefaultIds = getDefaultPins(role);
  const firstDefaultRoute = roleDefaultIds
    .map((defaultId) => pinnableVisibleRoutes.find((route) => route.id === defaultId))
    .find(Boolean) || null;

  const firstRecommendation = firstDefaultRoute || pinnableVisibleRoutes[0] || null;
  const secondRecommendation = pinnableVisibleRoutes.find(
    (route) => route.id !== firstRecommendation?.id
  ) || null;

  const recommendations = [];

  if (firstRecommendation) {
    recommendations.push({
      type: 'route',
      id: firstRecommendation.id,
      label: firstRecommendation.label,
    });
  }

  if (secondRecommendation) {
    recommendations.push({
      type: 'route',
      id: secondRecommendation.id,
      label: secondRecommendation.label,
    });
  }

  if (recommendations.length < 2) {
    recommendations.push(GENERIC_PIN_RECOMMENDATION);
  }

  return recommendations.slice(0, 2);
};

const toRecentTimestamp = (recentRoute) => {
  const timestamp = Number(new Date(recentRoute?.timestamp || 0));
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export const computeTryItPinRoute = ({
  visibleRoutes = [],
  pinnedRoutes = [],
  recentRoutes = [],
}) => {
  const pinnedRouteSet = new Set(Array.isArray(pinnedRoutes) ? pinnedRoutes : []);
  const pinnableVisibleRoutes = uniqueById(visibleRoutes).filter(isPinnableRoute);
  const visibleRouteMap = new Map(pinnableVisibleRoutes.map((route) => [route.id, route]));
  const orderedCandidates = PIN_SUGGESTION_ROUTE_IDS
    .map((routeId) => visibleRouteMap.get(routeId))
    .filter(Boolean)
    .filter((route) => !pinnedRouteSet.has(route.id));

  if (orderedCandidates.length === 0) {
    return null;
  }

  const routeOrderById = new Map(
    PIN_SUGGESTION_ROUTE_IDS.map((routeId, index) => [routeId, index])
  );
  const recentTimestampById = new Map();

  (Array.isArray(recentRoutes) ? recentRoutes : []).forEach((recentRoute) => {
    const routeId = recentRoute?.routeId;
    if (!routeId || !routeOrderById.has(routeId)) return;
    const timestamp = toRecentTimestamp(recentRoute);
    const previousTimestamp = recentTimestampById.get(routeId) || 0;
    if (timestamp > previousTimestamp) {
      recentTimestampById.set(routeId, timestamp);
    }
  });

  const recentlyViewedCandidates = orderedCandidates.filter((route) => recentTimestampById.has(route.id));
  if (recentlyViewedCandidates.length === 0) {
    return orderedCandidates[0];
  }

  return recentlyViewedCandidates.sort((left, right) => {
    const timestampDiff = (recentTimestampById.get(right.id) || 0) - (recentTimestampById.get(left.id) || 0);
    if (timestampDiff !== 0) return timestampDiff;
    return (routeOrderById.get(left.id) || 0) - (routeOrderById.get(right.id) || 0);
  })[0];
};

/**
 * Compact authenticated-only discovery banner for:
 * 1) Role-aware pinned view recommendations.
 * 2) AI Deal Summary voiceover discoverability.
 */
export default function NavigationUpdateBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [isLocalDismissed, setIsLocalDismissed] = useState(false);
  const { currUser, dataStatus } = useAppContext();
  const { routes: allRoutes } = useRouteRegistry();
  const {
    pinnedRoutes,
    recentRoutes,
    pinRoute,
    navigationPreferences,
    dismissNavigationBanner,
    hasLoadedPreferences,
  } = useUserRoutePreferences();

  const isDismissed = navigationPreferences?.[`navigationBanner${BANNER_VERSION}Dismissed`];

  useEffect(() => {
    if (hasLoadedPreferences && !isDismissed && !isLocalDismissed) {
      setIsVisible(true);
    }
  }, [hasLoadedPreferences, isDismissed, isLocalDismissed]);

  const visibleRoutes = useMemo(() => {
    if (!currUser || !Array.isArray(allRoutes)) return [];

    return getVisibleRoutes(
      currUser,
      dataStatus?.routePreferences || {},
      { pinnedRoutes: pinnedRoutes || [] },
      allRoutes
    );
  }, [allRoutes, currUser, dataStatus?.routePreferences, pinnedRoutes]);

  const tryItTargetRoute = useMemo(
    () => computeTryItPinRoute({
      visibleRoutes,
      pinnedRoutes,
      recentRoutes,
    }),
    [visibleRoutes, pinnedRoutes, recentRoutes]
  );

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      setIsLocalDismissed(true);
    }, 200);

    if (dismissNavigationBanner?.mutate) {
      dismissNavigationBanner.mutate(BANNER_VERSION);
    }
  };
  const handleTryIt = () => {
    if (!tryItTargetRoute?.id || !pinRoute?.mutate) return;
    pinRoute.mutate(tryItTargetRoute.id);
  };

  if (!hasLoadedPreferences) {
    return null;
  }

  if (isDismissed || isLocalDismissed || !tryItTargetRoute) {
    return null;
  }

  return (
    <BannerContainer
      isVisible={isVisible}
      elevation={0}
      sx={{
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.2s ease-out',
      }}
    >
      <Box sx={{
        py: 1.25,
        px: { xs: 2, md: 3 },
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        maxWidth: 1200,
        mx: 'auto'
      }}>
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            lineHeight: 1.4,
            fontSize: '0.875rem',
            flex: 1,
            minWidth: 0,
          }}
        >
          <strong>New on QFlow:</strong> Pin key views by hovering and clicking the pin icon, and click any opportunity name for an AI Deal Summary voiceover.
        </Typography>
        {tryItTargetRoute?.label && (
          <Button
            size="small"
            onClick={handleTryIt}
            disabled={Boolean(pinRoute?.isPending)}
            sx={{
              textTransform: 'none',
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              fontSize: '0.8125rem',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Pin {tryItTargetRoute.label}
          </Button>
        )}
        <Button
          size="small"
          onClick={handleDismiss}
          sx={{
            textTransform: 'none',
            minWidth: 'auto',
            px: 1,
            py: 0.5,
            fontSize: '0.8125rem',
            color: 'text.secondary',
            flexShrink: 0,
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          Dismiss
        </Button>
      </Box>
    </BannerContainer>
  );
}
